import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import bindMethods from 'auto-bind';
import ReactTable from 'react-table';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';

const UsersTableContainer = styled.table`
  border-collapse: collapse;
  width: 100%;

  td,
  th {
    text-align: left;
    padding: 14px 0px;
  }

  th {
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    color: ${props => props.theme.BLACK};
  }

  tbody {
    tr {
      cursor: pointer;
    }

    tr:nth-child(odd) {
      background-color: ${props => props.theme.NEUTRAL_GREY};
    }
  }
`;

const StyledNameCell = styled.td`
  font-size: 14px;
  font-weight: bold;
  font-style: normal;
  color: ${props => props.theme.BLACK};
`;

const StyledEmailCell = styled.td`
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  color: ${props => props.theme.BLACK};
`;

const StyledStatusCell = styled.td`
  display: flex;
  justify-content: start;
`;

const StyledStatusPill = styled.div`
  width: 85px;
  height: 20px;
  border-radius: 30px;
  background-color: ${props => (props.status ? props.theme.SAFE : props.theme.WARNING)};
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    font-size: 12px;
    font-weight: bold;
    color: ${props => props.theme.WHITE};
  }
`;

const SearchInputField = styled.input`
  width: 385px;
  height: 36px;
  border-radius: 1px;
  border: ${props => `solid 1px ${props.theme.GREY}`};
  padding: 8px 11px;
  font-size: 14px;
  font-weight: normal;
  margin-bottom: 10px;
`;

class UsersTable extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);

    this.state = {
      users: props.users,
    };

    this.TABLE_COLUMNS = [
      {
        Header: 'Name',
        accessor: 'displayName',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
    ];
  }

  handleSearch(event) {
    const { value } = event.target;
    const { users: propUsers } = this.props;

    this.setState({
      users: propUsers.filter((user) => {
        const userName = user.displayName.toLowerCase();
        const userEmail = user.email.toLowerCase();
        return userName.includes(value.toLowerCase()) || userEmail.includes(value.toLowerCase());
      }),
    });
  }

  handleTableRowClick(row) {
    const { history } = this.props;
    const { id } = row;

    history.push(`/users/${id}`);
  }

  renderNameCell(row, index) {
    return (
      <StyledNameCell
        id={`UserTableRow.displayName.${index}`}
        key={`UserTableRow.displayName.${row.id}`}
      >
        {row.displayName}
      </StyledNameCell>
    );
  }

  renderEmailCell(row, index) {
    return (
      <StyledEmailCell key={`UserTableRow.email.${row.id}`} id={`UserTableRow.email.${index}`}>
        {row.email}
      </StyledEmailCell>
    );
  }

  renderStatusCell(row, index) {
    const { t } = this.props;

    const term = row.status ? t('Shared.General.Active') : t('Shared.General.Inactive');
    return (
      <StyledStatusCell key={`UserTableRow.status.${row.id}`} id={`UserTableRow.status.${index}`}>
        <StyledStatusPill status={row.status}>
          <span>{term}</span>
        </StyledStatusPill>
      </StyledStatusCell>
    );
  }

  renderTableRow(row, columns, index) {
    return (
      <tr
        id={`UserTableRow.${index}`}
        key={`UserTableRow.${row.id}`}
        onClick={this.handleTableRowClick.bind(null, row)}
      >
        {columns.map((column) => {
          switch (column.accessor) {
            case 'displayName':
              return this.renderNameCell(row, index);
            case 'email':
              return this.renderEmailCell(row, index);
            case 'status':
              return this.renderStatusCell(row, index);
            default:
              return null;
          }
        })}
      </tr>
    );
  }

  // eslint-disable-next-line no-unused-vars
  renderUsersTable(state, makeTable, instance) {
    const { t } = this.props;
    const { columns, data } = state;

    return (
      <>
        <SearchInputField
          id="Pages.Users.SearchUser"
          placeholder={t('Pages.Users.Actions.SearchUser')}
          onChange={this.handleSearch}
        />
        <UsersTableContainer id="UsersTable">
          <thead>
            <tr>
              {columns.map(column => (
                <th key={`UsersTable.${column.Header}`} id={`UsersTable.Header.${column.Header}`}>
                  {column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{data.map((row, index) => this.renderTableRow(row, columns, index))}</tbody>
        </UsersTableContainer>
      </>
    );
  }

  render() {
    const { users } = this.state;

    return (
      <ReactTable columns={this.TABLE_COLUMNS} data={users}>
        {this.renderUsersTable}
      </ReactTable>
    );
  }
}

UsersTable.propTypes = {
  history: PropTypes.object.isRequired, // supplied from withRouter
  t: PropTypes.func.isRequired, // supplied from withTranslation
  users: PropTypes.array.isRequired,
};

const TranslatedComponent = withTranslation()(UsersTable);
export default withRouter(TranslatedComponent);
